import { ErrorMessage } from "@hookform/error-message";
import * as moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  UncontrolledDropdown,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  Row,
  RSelect,
} from "../../components/Component";
import Table from "../../components/react-table/Table";
import { DELETE, GET, POST, PUT } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { INGREDIENT_CATEGORY_ID, INGREDIENT_CATEGORY_NAME, TRUCK_TYPE, TRUCK_TYPE_NAME } from '../../utils/Utils';

const INGREDIENT_CATEGORY_LIST = Object.values(INGREDIENT_CATEGORY_ID);

const DEFAULT_TRUCK = {
  id: "",
  type: TRUCK_TYPE.TRUCK,
  weight: 0,
  defaultDriverId: null,
  defaultSubDriverId: null,
  defaultWeightByIngredient: {},
};


const TRUCK_TYPE_OPTIONS = Object.keys(TRUCK_TYPE).map((c) => ({
  label: TRUCK_TYPE_NAME[c],
  value: c,
}));

const TruckManager = () => {
  const clearPagi = useRef();
  const [data, setData] = useState([]);
  const [view, setView] = useState({
    edit: false,
    add: false,
    isDelete: false,
  });
  const { errors, register, handleSubmit, reset, getValues, control } = useForm(
    {
      defaultValues: DEFAULT_TRUCK,
    }
  );

  const [loading, setLoading] = useState(false);
  const [onSearchText, setSearchText] = useState("");
  const [editId, setEditedId] = useState();
  const [deleteId, setDeleteId] = useState();
  const [timeNow, setTimeNow] = useState();
  const [smOption, setSmOption] = useState(false);
  const [driverOptions, setDriverOptions] = useState([]);

  useEffect(() => {
    GET("/user/list-drivers", {}, true)
      .then(({ data }) => {
        setDriverOptions((data || []).map((c) => ({ label: c.name, value: c.id })));
      })
      .catch(() => { });
  }, []);

  const colTable = [
    {
      Header: "Biển số xe",
      accessor: "id",
    },
    {
      Header: "Loại",
      accessor: (item) => TRUCK_TYPE_NAME[item.type],
    },
    {
      Header: "Khối lượng tải (kg)",
      accessor: (item) => <NumericFormat
        displayType={"text"}
        value={item.weight || 0}
        thousandsGroupStyle="thousand"
        thousandSeparator=","
        decimalScale={2}
      />,
    },
    {
      Header: "Hành động",
      accessor: (item) => (
        <ul className="nk-tb-actions gx-1">
          <li>
            <UncontrolledDropdown>
              <DropdownToggle
                tag="a"
                className="btn btn-icon dropdown-toggle btn-trigger"
              >
                <Icon name="more-h"></Icon>
              </DropdownToggle>
              <DropdownMenu right>
                <ul className="link-list-opt no-bdr">
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#dropdown"
                      onClick={(ev) => {
                        ev.preventDefault();
                        onEditClick(item.id);
                        toggle("edit");
                      }}
                    >
                      <Icon name="edit" className="text-primary"></Icon>
                      <span>Sửa</span>
                    </DropdownItem>

                  </li>
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#dropdown"
                      onClick={(ev) => {
                        ev.preventDefault();
                        setDeleteId(item.id);
                        toggle("isDelete");
                      }}
                    >
                      <Icon name="trash" className="text-danger"></Icon>
                      <span>Xoá</span>
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </li>
        </ul>
      ),
    },
  ];

  // Changing state value when searching name
  useEffect(() => {
    setLoading(true);
    setData([]);
    clearTimeout(clearPagi.current);
    clearPagi.current = setTimeout(() => {
      let params = {
      };
      if (onSearchText !== "") {
        params.search = onSearchText;
      }

      GET(`truck`, params, true)
        .then(({ data }) => {
          setData([...data?.rows]);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 400);
  }, [onSearchText, timeNow]);

  // toggle function to view order details
  const toggle = (type) => {
    setView({
      ...view,
      [type]: !view[type],
    });
  };

  const resetData = () => {
    setEditedId("");
    onFormCancel();
    setSearchText("");
    setTimeNow(moment().valueOf());
  };

  // Submits form data
  const onHandleSubmit = () => {
    setLoading(true);
    const { id, type, weight, defaultDriverId, defaultSubDriverId, defaultWeightByIngredient } = getValues();

    const cleanedWeightByIngredient = Object.keys(defaultWeightByIngredient || {}).reduce((acc, key) => {
      const val = defaultWeightByIngredient[key];
      if (val !== null && val !== undefined && val !== "") {
        acc[key] = Number(val);
      }
      return acc;
    }, {});

    const formData = {
      id,
      type,
      weight,
      defaultDriverId: defaultDriverId ?? null,
      defaultSubDriverId: defaultSubDriverId ?? null,
      defaultWeightByIngredient: cleanedWeightByIngredient,
    };

    const request = editId
      ? PUT(`truck/${editId}`, formData, {}, true)
      : POST("truck", formData, {}, true);

    request
      .then((res) => {
        if (editId) {
          toast.success("Cập nhật thành công");
        } else {
          toast.success("Thêm thành công");
        }
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // onChange function for searching name
  const onFilterChange = (e) => {
    setSearchText(e.target.value);
  };

  // function to close the form modal
  const onFormCancel = () => {
    setView({ add: false, edit: false, isDelete: false });
    reset(DEFAULT_TRUCK);
  };

  const deleteOrder = (id) => {
    if (!id) {
      return;
    }
    DELETE(`truck/${id}`, {}, true)
      .then((res) => {
        toast.success("Xóa xe tải thành công");
        resetData();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      });
  };

  const onEditClick = (id) => {
    setLoading(true);
    setEditedId(id);

    GET(`/truck/${id}`, {
    }, true)
      .then(({ data }) => {
        reset({
          ...data,
        });
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
        onFormCancel();

      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <React.Fragment>
      <Head title="Quản lý xe"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Xe tải</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <Icon name="search"></Icon>
                        </div>
                        <input
                          type="text"
                          placeholder="Tìm kiếm xe"
                          className="form-control  border-primary"
                          value={onSearchText}
                          onChange={(e) => onFilterChange(e)}
                        />
                      </div>
                    </li>

                    <li>
                      <div>
                        <Button
                          className="btn-primary"
                          color="primary"
                          onClick={() => {
                            resetData();
                            toggle("add");
                          }}
                        >
                          <Icon name="plus"></Icon>
                          <span>Thêm xe</span>
                        </Button>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <div
                id="DataTables_Table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4 no-footer"
              >
                <div className="row justify-between g-2">
                  <div className="col-8 col-sm-6 text-start"></div>
                  <div className="col-5 col-sm-6 text-end">
                    <div className="datatable-filter">
                      <div className="d-flex justify-content-end g-2">
                        <div
                          className="dataTables_length"
                          id="DataTables_Table_1_length"
                        >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="my-3">
                  <Table
                    loading={loading}
                    columns={colTable}
                    data={data}
                    notShowPagi={true}
                  ></Table>
                </div>
              </div>
            </div>
          </div>
        </Block>

        <Modal
          isOpen={view.add || view.edit}
          toggle={() => onFormCancel()}
          className="modal-dialog-centered"
          size="lg"
        >
          <ModalBody>
            <a href="#cancel" className="close">
              {" "}
              <Icon
                name="cross-sm"
                onClick={(ev) => {
                  ev.preventDefault();
                  onFormCancel();
                }}
              ></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">{editId ? "Sửa xe" : "Thêm xe"}</h5>
              <div className="mt-4">
                <form onSubmit={handleSubmit(onHandleSubmit)}>
                  <Row className="g-3">
                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label" htmlFor="customer">
                          Biển số xe <span className="text-danger">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            name="id"
                            ref={register({
                              required: "Vui lòng nhập thông tin",
                            })}
                            disabled={editId}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="id"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label" >
                          Loại <span className="text-danger">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="type"
                            rules={{ required: "Vui lòng nhập thông tin" }}
                            render={({ onChange, value, name, ref }) => (
                              <RSelect
                                isDisabled={!!editId}
                                ref={ref}
                                name={name}
                                options={TRUCK_TYPE_OPTIONS}
                                value={TRUCK_TYPE_OPTIONS.find(
                                  (c) => c.value === value
                                )}
                                onChange={(val) => onChange(val.value)}
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="type"
                            render={({ message }) => (
                              <span className="invalid">{message}</span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label" htmlFor="interestRate">
                          Khối lượng xe(kg)
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="weight"
                            rules={{ required: "Vui lòng nhập thông tin" }}
                            render={({ onChange, value, ref }) => (
                              <NumericFormat
                                className="form-control"
                                inputRef={ref}
                                type="text"
                                placeholder="10"
                                value={value}
                                thousandsGroupStyle="thousand"
                                thousandSeparator=","
                                decimalScale={2}
                                onValueChange={(val) =>
                                  onChange(val.floatValue)
                                }
                              />
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="form-group">
                        <label className="form-label">
                          Tài xế mặc định
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="defaultDriverId"
                            render={({ onChange, value, name, ref }) => (
                              <RSelect
                                ref={ref}
                                name={name}
                                isClearable
                                placeholder="Chọn tài xế..."
                                options={driverOptions}
                                value={driverOptions.find((c) => c.value === value) || null}
                                onChange={(val) => onChange(val?.value ?? null)}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="form-group">
                        <label className="form-label">
                          Tài xế phụ mặc định
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            name="defaultSubDriverId"
                            render={({ onChange, value, name, ref }) => (
                              <RSelect
                                ref={ref}
                                name={name}
                                isClearable
                                placeholder="Chọn tài xế phụ..."
                                options={driverOptions}
                                value={driverOptions.find((c) => c.value === value) || null}
                                onChange={(val) => onChange(val?.value ?? null)}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label">
                          Khối lượng tải mặc định theo nguyên liệu (kg)
                        </label>
                        <Row className="g-2">
                          {INGREDIENT_CATEGORY_LIST.map((cateId) => (
                            <Col md="6" key={cateId}>
                              <div className="form-group">
                                <label className="form-label form-label-sm">
                                  {INGREDIENT_CATEGORY_NAME[cateId] || cateId}
                                </label>
                                <div className="form-control-wrap">
                                  <Controller
                                    control={control}
                                    name={`defaultWeightByIngredient.${cateId}`}
                                    defaultValue={null}
                                    render={({ onChange, value, ref }) => (
                                      <NumericFormat
                                        className="form-control"
                                        inputRef={ref}
                                        type="text"
                                        placeholder="0"
                                        value={value ?? ""}
                                        thousandsGroupStyle="thousand"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        onValueChange={(val) =>
                                          onChange(val.floatValue ?? null)
                                        }
                                      />
                                    )}
                                  />
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </Col>
                    <Col size="12">
                      <Button color="primary" type="submit">
                        <Icon className="plus"></Icon>
                        <span>{editId ? "Cập nhật" : "Thêm xe"}</span>
                      </Button>
                    </Col>
                  </Row>
                </form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={view.isDelete} toggle={() => onFormCancel()}>
          <ModalBody className="modal-body-lg text-center">
            <div className="nk-modal">
              <Icon className="nk-modal-icon icon-circle icon-circle-xxl ni ni-cross bg-danger"></Icon>
              <h4 className="nk-modal-title">
                Bạn có chắc muốn xoá xe này không ?{" "}
              </h4>
              <div className="nk-modal-action mt-5 d-flex justify-content-around">
                <Button
                  color="light"
                  size="lg"
                  className="btn-mw mr-5"
                  onClick={() => onFormCancel()}
                >
                  Huỷ bỏ
                </Button>
                <Button
                  color="primary"
                  size="lg"
                  className="btn-mw"
                  onClick={(ev) => {
                    ev.preventDefault();
                    deleteOrder(deleteId);
                  }}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default TruckManager;
